import { UseQueryResult, useQuery } from '@tanstack/react-query'
import { lazy, Suspense, type ReactElement } from 'react'
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom'
import AppShell from './layout/AppShell'
import { apiGet, apiPost, Me, Role } from './api'

const LoginPage = lazy(() => import('./pages/LoginPage'))
const Dashboard = lazy(() => import('./pages/Dashboard'))
const NotFound = lazy(() => import('./pages/NotFound'))
const CustomersPage = lazy(() => import('./pages/admin/CustomersPage'))
const ClustersPage = lazy(() => import('./pages/admin/ClustersPage'))
const MaintenanceWindowsPage = lazy(() => import('./pages/admin/MaintenanceWindowsPage'))
const PatchSchedulesPage = lazy(() => import('./pages/admin/PatchSchedulesPage'))
const ProjectsPage = lazy(() => import('./pages/admin/ProjectsPage'))
const ForecastProjectsPage = lazy(() => import('./pages/admin/ForecastProjectsPage'))
const MisPackagesPage = lazy(() => import('./pages/admin/MisPackagesPage'))
const MisSubscriptionsPage = lazy(() => import('./pages/admin/MisSubscriptionsPage'))
const UsersPage = lazy(() => import('./pages/admin/UsersPage'))
const CustomerHealthPage = lazy(() => import('./pages/admin/CustomerHealthPage'))
const TimeEntriesPage = lazy(() => import('./pages/time/TimeEntriesPage'))
const ApprovalsPage = lazy(() => import('./pages/approvals/ApprovalsPage'))
const BalanceReportPage = lazy(() => import('./pages/reports/BalanceReportPage'))
const RevenueReportPage = lazy(() => import('./pages/reports/RevenueReportPage'))
const InternalRevenueReportPage = lazy(() => import('./pages/reports/InternalRevenueReportPage'))
const TimeTrackingReportPage = lazy(() => import('./pages/reports/TimeTrackingReportPage'))
const MisForecastReportPage = lazy(() => import('./pages/reports/MisForecastReportPage'))
const TeamUtilizationReportPage = lazy(() => import('./pages/reports/TeamUtilizationReportPage'))
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'))
const ReleaseNotesPage = lazy(() => import('./pages/ReleaseNotesPage'))

function useMe() {
  return useQuery({ queryKey: ['me'], queryFn: () => apiGet<Me>('/api/auth/me'), retry: false })
}

function RequireAuth({ meQuery, children }: { meQuery: UseQueryResult<Me, Error>; children: ReactElement }) {
  const loc = useLocation()
  if (meQuery.isLoading) return <AppShell title="Loading…" />
  if (meQuery.isError || !meQuery.data) return <Navigate to="/login" replace state={{ from: `${loc.pathname}${loc.search}` }} />
  return children
}

function RequireRoles({ me, roles, children }: { me?: Me; roles: Role[]; children: ReactElement }) {
  if (!me || !roles.includes(me.role)) {
    return <Navigate to="/" replace />
  }
  return children
}

function PageFallback() {
  return <AppShell title="Loading…" />
}

function ProtectedLayout({ me, onLogout }: { me?: Me; onLogout: () => Promise<void> }) {
  const location = useLocation()

  return (
    <AppShell me={me} onLogout={onLogout} title="MIS Service Hub">
      <Suspense key={`${location.pathname}${location.search}`} fallback={<PageFallback />}>
        <Outlet />
      </Suspense>
    </AppShell>
  )
}

export default function App() {
  const meQuery = useMe()

  async function logout() {
    try {
      await apiPost('/logout')
    } finally {
      window.location.href = '/login'
    }
  }

  return (
    <Routes>
      <Route
        path="/login"
        element={(
          <Suspense fallback={<PageFallback />}>
            <AppShell title="MIS Service Hub">
              <LoginPage />
            </AppShell>
          </Suspense>
        )}
      />
      <Route
        path="/"
        element={(
          <RequireAuth meQuery={meQuery}>
            <ProtectedLayout me={meQuery.data} onLogout={logout} />
          </RequireAuth>
        )}
      >
        <Route index element={<Dashboard />} />
        <Route path="time" element={<TimeEntriesPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
        <Route path="release-notes" element={<ReleaseNotesPage />} />
        <Route path="approvals" element={<RequireRoles me={meQuery.data} roles={['MANAGER', 'ADMIN', 'SUPER_ADMIN']}><ApprovalsPage /></RequireRoles>} />
        <Route path="reports/balance" element={<RequireRoles me={meQuery.data} roles={['MANAGER', 'ADMIN', 'SUPER_ADMIN']}><BalanceReportPage /></RequireRoles>} />
        <Route path="reports/revenue" element={<RequireRoles me={meQuery.data} roles={['MANAGER', 'ADMIN', 'SUPER_ADMIN']}><RevenueReportPage /></RequireRoles>} />
        <Route path="reports/internal-revenue" element={<RequireRoles me={meQuery.data} roles={['SUPER_ADMIN']}><InternalRevenueReportPage /></RequireRoles>} />
        <Route path="reports/time-entries" element={<RequireRoles me={meQuery.data} roles={['MANAGER', 'ADMIN', 'SUPER_ADMIN']}><TimeTrackingReportPage /></RequireRoles>} />
        <Route path="reports/mis-forecast" element={<RequireRoles me={meQuery.data} roles={['MANAGER', 'ADMIN', 'SUPER_ADMIN']}><MisForecastReportPage /></RequireRoles>} />
        <Route path="reports/team-utilization" element={<RequireRoles me={meQuery.data} roles={['MANAGER', 'ADMIN', 'SUPER_ADMIN']}><TeamUtilizationReportPage /></RequireRoles>} />
        <Route path="admin" element={<Navigate to="/admin/customers" replace />} />
        <Route path="admin/customers" element={<RequireRoles me={meQuery.data} roles={['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN']}><CustomersPage /></RequireRoles>} />
        <Route path="admin/clusters" element={<RequireRoles me={meQuery.data} roles={['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN']}><ClustersPage /></RequireRoles>} />
        <Route path="admin/maintenance-windows" element={<RequireRoles me={meQuery.data} roles={['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN']}><MaintenanceWindowsPage /></RequireRoles>} />
        <Route path="admin/patch-schedules" element={<RequireRoles me={meQuery.data} roles={['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN']}><PatchSchedulesPage /></RequireRoles>} />
        <Route path="admin/projects" element={<RequireRoles me={meQuery.data} roles={['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN']}><ProjectsPage /></RequireRoles>} />
        <Route path="admin/forecasts" element={<RequireRoles me={meQuery.data} roles={['MIS_ENGINEER', 'MANAGER', 'ADMIN', 'SUPER_ADMIN']}><ForecastProjectsPage /></RequireRoles>} />
        <Route path="admin/mis-packages" element={<RequireRoles me={meQuery.data} roles={['ADMIN', 'SUPER_ADMIN']}><MisPackagesPage /></RequireRoles>} />
        <Route path="admin/mis-subscriptions" element={<RequireRoles me={meQuery.data} roles={['ADMIN', 'SUPER_ADMIN']}><MisSubscriptionsPage /></RequireRoles>} />
        <Route path="admin/tiers" element={<Navigate to="/admin/mis-packages" replace />} />
        <Route path="admin/users" element={<RequireRoles me={meQuery.data} roles={['ADMIN', 'SUPER_ADMIN']}><UsersPage /></RequireRoles>} />
        <Route path="admin/customer-health" element={<RequireRoles me={meQuery.data} roles={['ADMIN', 'SUPER_ADMIN']}><CustomerHealthPage /></RequireRoles>} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  )
}
